/**
 * Cloudflare Turnstile challenge for the self-service signup screen.
 *
 * Renders the widget into a host div via a ref callback (React 19: the
 * callback returns its own cleanup), reporting a fresh token through
 * `onToken` and `null` when the token expires or the challenge errors.
 *
 * `onToken` must be referentially stable (`useCallback` / a state setter) — a
 * new identity re-runs the ref callback and remounts the widget.
 */
import { useCallback } from 'react';

import { logger } from '@/lib/logger';
import { loadTurnstile } from '@/lib/self-service/turnstile';

interface TurnstileWidgetProps {
  siteKey: string;
  onToken: (token: string | null) => void;
}

export function TurnstileWidget({ siteKey, onToken }: TurnstileWidgetProps) {
  const mount = useCallback(
    (host: HTMLDivElement | null) => {
      if (!host) {
        return undefined;
      }
      let cancelled = false;
      let widgetId: string | null = null;

      loadTurnstile()
        .then(api => {
          if (cancelled) {
            return;
          }
          widgetId = api.render(host, {
            sitekey: siteKey,
            callback: onToken,
            'expired-callback': () => onToken(null),
            'error-callback': () => onToken(null),
          });
        })
        .catch((error: unknown) => {
          logger.error(
            'TurnstileWidget: failed to load Turnstile',
            error instanceof Error ? error : new Error(String(error))
          );
          onToken(null);
        });

      return () => {
        cancelled = true;
        if (widgetId !== null) {
          window.turnstile?.remove(widgetId);
        }
      };
    },
    [siteKey, onToken]
  );

  // Turnstile's managed widget is 65px tall; reserve it so the buttons below
  // don't jump when the challenge renders.
  return <div ref={mount} className='min-h-[65px]' data-testid='turnstile' />;
}
